import "core-js/modules/es.symbol.description.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { makeObservable } from "mobx";
import Cartesian2 from "terriajs-cesium/Source/Core/Cartesian2";
import Cartographic from "terriajs-cesium/Source/Core/Cartographic";
import Credit from "terriajs-cesium/Source/Core/Credit";
import Ellipsoid from "terriajs-cesium/Source/Core/Ellipsoid";
import CesiumEvent from "terriajs-cesium/Source/Core/Event";
import GeographicProjection from "terriajs-cesium/Source/Core/GeographicProjection";
import GeographicTilingScheme from "terriajs-cesium/Source/Core/GeographicTilingScheme";
import Math from "terriajs-cesium/Source/Core/Math";
import Resource from "terriajs-cesium/Source/Core/Resource";
import DiscardMissingTileImagePolicy from "terriajs-cesium/Source/Scene/DiscardMissingTileImagePolicy";
import ImageryLayerFeatureInfo from "terriajs-cesium/Source/Scene/ImageryLayerFeatureInfo";
import ImageryProvider from "terriajs-cesium/Source/Scene/ImageryProvider";
/** This is adapted from Cesium's ArcGisMapServerImageryProvider
 * https://github.com/CesiumGS/cesium/blob/51aae2d21014cfc28e948b1719d07f1912df9434/packages/engine/Source/Scene/ArcGisMapServerImageryProvider.js
 * Code licensed under the Apache License v2.0.
 * For details, see https://github.com/CesiumGS/cesium/blob/main/LICENSE.md
 */

export default class ArcGisImageServerImageryProvider {
  constructor(options) {
    var _options$tilingScheme, _options$rectangle, _options$tileWidth, _options$tileHeight, _options$minimumLevel, _options$maximumLevel, _options$enablePickFe, _options$usePreCached;
    _defineProperty(this, "tilingScheme", void 0);
    _defineProperty(this, "ellipsoid", void 0);
    _defineProperty(this, "tileWidth", void 0);
    _defineProperty(this, "tileHeight", void 0);
    _defineProperty(this, "minimumLevel", void 0);
    _defineProperty(this, "maximumLevel", void 0);
    _defineProperty(this, "rectangle", void 0);
    _defineProperty(this, "errorEvent", new CesiumEvent());
    _defineProperty(this, "ready", true);
    _defineProperty(this, "credit", void 0);
    /** Note: this can be set dynamically */
    _defineProperty(this, "enablePickFeatures", void 0);
    _defineProperty(this, "usePreCachedTiles", void 0);
    _defineProperty(this, "tileDiscardPolicy", void 0);
    _defineProperty(this, "baseResource", void 0);
    _defineProperty(this, "defaultNightAlpha", undefined);
    _defineProperty(this, "defaultDayAlpha", undefined);
    _defineProperty(this, "hasAlphaChannel", true);
    _defineProperty(this, "defaultAlpha", undefined);
    _defineProperty(this, "defaultBrightness", undefined);
    _defineProperty(this, "defaultContrast", undefined);
    _defineProperty(this, "defaultGamma", undefined);
    _defineProperty(this, "defaultHue", undefined);
    _defineProperty(this, "defaultSaturation", undefined);
    _defineProperty(this, "defaultMagnificationFilter", undefined);
    _defineProperty(this, "defaultMinificationFilter", undefined);
    _defineProperty(this, "readyPromise", Promise.resolve(true));
    makeObservable(this);
    this.tilingScheme = (_options$tilingScheme = options.tilingScheme) !== null && _options$tilingScheme !== void 0 ? _options$tilingScheme : new GeographicTilingScheme();
    this.rectangle = (_options$rectangle = options.rectangle) !== null && _options$rectangle !== void 0 ? _options$rectangle : this.tilingScheme.rectangle;
    this.ellipsoid = Ellipsoid.WGS84;
    this.tileWidth = (_options$tileWidth = options.tileWidth) !== null && _options$tileWidth !== void 0 ? _options$tileWidth : 256;
    this.tileHeight = (_options$tileHeight = options.tileHeight) !== null && _options$tileHeight !== void 0 ? _options$tileHeight : 256;
    this.minimumLevel = (_options$minimumLevel = options.minimumLevel) !== null && _options$minimumLevel !== void 0 ? _options$minimumLevel : 0;
    this.maximumLevel = (_options$maximumLevel = options.maximumLevel) !== null && _options$maximumLevel !== void 0 ? _options$maximumLevel : 25;
    this.ready = true;
    this.credit = typeof options.credit === "string" ? new Credit(options.credit) : options.credit;
    this.enablePickFeatures = (_options$enablePickFe = options.enablePickFeatures) !== null && _options$enablePickFe !== void 0 ? _options$enablePickFe : true;
    this.usePreCachedTiles = (_options$usePreCached = options.usePreCachedTiles) !== null && _options$usePreCached !== void 0 ? _options$usePreCached : false;
    this.baseResource = new Resource(options.url);
    this.baseResource.appendForwardSlash();
    if (options.parameters) {
      this.baseResource.appendQueryParameters(options.parameters);
    }
    if (options.token) {
      this.baseResource.appendQueryParameters({
        token: options.token
      });
    }
    this.tileDiscardPolicy = new DiscardMissingTileImagePolicy({
      missingImageUrl: this.buildImageResource(0, 0, this.maximumLevel).url,
      pixelsToCheck: [new Cartesian2(0, 0), new Cartesian2(200, 20), new Cartesian2(20, 200), new Cartesian2(80, 110), new Cartesian2(160, 130)],
      disableCheckIfAllPixelsAreTransparent: true
    });
  }
  get proxy() {
    return this.baseResource.proxy;
  }
  buildImageResource(x, y, level, request) {
    if (this.usePreCachedTiles) {
      return this.baseResource.getDerivedResource({
        url: "tile/".concat(level, "/").concat(y, "/").concat(x),
        request: request
      });
    } else {
      const nativeRectangle = this.tilingScheme.tileXYToNativeRectangle(x, y, level);
      const bbox = "".concat(nativeRectangle.west, ",").concat(nativeRectangle.south, ",").concat(nativeRectangle.east, ",").concat(nativeRectangle.north);
      const query = {
        bbox: bbox,
        size: "".concat(this.tileWidth, ",").concat(this.tileHeight),
        format: "png32",
        transparent: true,
        f: "image"
      };
      if (this.tilingScheme.projection instanceof GeographicProjection) {
        query.bboxSR = 4326;
        query.imageSR = 4326;
      } else {
        query.bboxSR = 3857;
        query.imageSR = 3857;
      }
      return this.baseResource.getDerivedResource({
        url: "exportImage",
        queryParameters: query,
        request: request
      });
    }
  }
  getTileCredits() {
    return [];
  }
  requestImage(x, y, level, request) {
    return ImageryProvider.loadImage(this, this.buildImageResource(x, y, level, request));
  }
  async pickFeatures(_x, _y, _level, longitude, latitude) {
    if (!this.enablePickFeatures) {
      return [];
    }
    let horizontal;
    let vertical;
    let sr;
    if (this.tilingScheme.projection instanceof GeographicProjection) {
      horizontal = Math.toDegrees(longitude);
      vertical = Math.toDegrees(latitude);
      sr = "4326";
    } else {
      const projected = this.tilingScheme.projection.project(new Cartographic(longitude, latitude, 0.0));
      horizontal = projected.x;
      vertical = projected.y;
      sr = "3857";
    }
    const query = {
      f: "json",
      geometryType: "esriGeometryPoint",
      geometry: "{x: ".concat(horizontal, ", y: ").concat(vertical, ", spatialReference: {wkid: ").concat(sr, "}}"),
      // Disable catalog items - as we don't use them
      returnCatalogItems: false
    };
    const resource = this.baseResource.getDerivedResource({
      url: "identify",
      queryParameters: query
    });
    const json = await resource.fetchJson();
    const result = [];
    if (json.value) {
      const featureInfo = new ImageryLayerFeatureInfo();
      featureInfo.data = json;
      featureInfo.name = json.name;
      featureInfo.properties = json.properties;
      featureInfo.description = json.value;
      result.push(featureInfo);
    }

    // Todo: handle json.catalogItems

    return result;
  }
}